import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { type CardDispute } from '@column/api';
import { Button, Dropdown, Icon, formatNumber } from '@column/column-ui-kit';
import {
  Badge,
  DataTable,
  type DataTableColumnDef,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Timestamp,
} from '@column/ui';

import { FilterDropdown } from '~/app/pages/Transfers/Overview';
import { ROUTE } from '~/app/routes';
import { Loading, Pagination } from '~/components';
import { LogoLoading } from '~/elements';
import { useQueryParams } from '~/hooks/useQueryParams';
import { useCanManageCardDisputes, useCardDisputes } from '~/lib/api/cardDisputes';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import {
  CARD_DISPUTE_STAGES,
  CARD_DISPUTE_STAGE_LABELS,
  CARD_DISPUTE_SUBMISSION_STATUSES,
  CARD_DISPUTE_SUBMISSION_STATUS_LABELS,
  type CardDisputeStage,
  type CardDisputeSubmissionStatus,
  formatRespondBy,
  getDisputeReasonLabel,
  getDisputeStageLabel,
  getDisputeStatusLabel,
  getSubmissionStatusLabel,
  getWorkflowLabel,
} from '~/lib/disputes/labels';
import { useNavigate } from '~/lib/navigation';
import { getTransferBadgeClassName } from '~/lib/transfers/utils';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { useTableConfig } from '~/stores/Config';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Flex, Inner } from '~/styles';
import { formatDateTime, getFormattedDate } from '~/util';

import { IssuerDashboardGate } from './IssuerDashboardGate';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: wrap;
  padding-bottom: 12px;
`;

const ToolbarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ResetFilter = styled(Button)`
  padding-left: 0;
  padding-right: 0;
`;

const DisputeFilterDropdown = styled(FilterDropdown)`
  min-width: 0;
`;

const STAGE_OPTIONS = CARD_DISPUTE_STAGES.map((stage) => ({
  value: stage,
  label: CARD_DISPUTE_STAGE_LABELS[stage],
}));

const SUBMISSION_STATUS_OPTIONS = CARD_DISPUTE_SUBMISSION_STATUSES.map((submissionStatus) => ({
  value: submissionStatus,
  label: CARD_DISPUTE_SUBMISSION_STATUS_LABELS[submissionStatus],
}));

type StageFilter = '' | CardDisputeStage;
type SubmissionStatusFilter = '' | CardDisputeSubmissionStatus;

interface CardDisputesFilters {
  stage: StageFilter;
  submission_status: SubmissionStatusFilter;
}

interface CardDisputesFilterUrlParams {
  stage?: string;
  submission_status?: string;
  starting_after?: string;
  page_index?: string;
}

const EMPTY_FILTERS: CardDisputesFilters = { stage: '', submission_status: '' };

const isStageFilter = (value: string): value is StageFilter =>
  value === '' || STAGE_OPTIONS.some((option) => option.value === value);

const isSubmissionStatusFilter = (value: string): value is SubmissionStatusFilter =>
  value === '' || SUBMISSION_STATUS_OPTIONS.some((option) => option.value === value);

const filtersFromUrl = (params: CardDisputesFilterUrlParams | undefined): CardDisputesFilters => {
  if (!params) return EMPTY_FILTERS;

  return {
    stage: params.stage && isStageFilter(params.stage) ? params.stage : '',
    submission_status:
      params.submission_status && isSubmissionStatusFilter(params.submission_status) ? params.submission_status : '',
  };
};

const filtersToUrl = (filters: CardDisputesFilters): CardDisputesFilterUrlParams => {
  const params: CardDisputesFilterUrlParams = {};
  if (filters.stage) params.stage = filters.stage;
  if (filters.submission_status) params.submission_status = filters.submission_status;
  return params;
};

const PageCardDisputesContent: React.FC = () => {
  const navigate = useNavigate();
  const { timeFormat, update: updateTableConfig } = useTableConfig('card-disputes');
  const tableAreaRef = useRef<HTMLDivElement>(null);
  const paginationRef = useRef<HTMLDivElement>(null);
  const hasMeasuredPageSize = useRef(false);
  const [pageSize, setPageSize] = useState(10);
  const { addDangerNotification } = useNotificationStore();
  const { currentPlatform, isSandbox } = useSessionStore();
  const canManage = useCanManageCardDisputes();
  const { queryParams, setQueryParams } = useQueryParams<CardDisputesFilterUrlParams>();
  const initialPagination = useRef({
    cursor: queryParams?.starting_after ? String(queryParams.starting_after) : undefined,
    pageIndex: Number(queryParams?.page_index) || 0,
  });
  const {
    cursor,
    onNext,
    onPrevious,
    reset: resetPagination,
    pageIndex,
    hasPrevious,
  } = usePaginationState({
    initialCursor: initialPagination.current.cursor,
    initialPageIndex: initialPagination.current.pageIndex,
  });

  const [filters, setFilters] = useState<CardDisputesFilters>(() => filtersFromUrl(queryParams));

  useLayoutEffect(() => {
    const tableArea = tableAreaRef.current;
    const pagination = paginationRef.current;
    const pageFrame = tableArea?.closest('[data-slot="page-frame"]');
    if (!tableArea || !pagination || !pageFrame) return;

    const updatePageSize = () => {
      const table = tableArea.querySelector<HTMLElement>('[data-slot="data-table"]');
      const header = tableArea.querySelector('[data-slot="data-table-header"]');
      const row = tableArea.querySelector('[data-slot="data-table-row"]');
      const headerHeight = header?.getBoundingClientRect().height || 41;
      const rowHeight = row?.getBoundingClientRect().height || 44;
      const bottomPadding = 24;
      const tableBorders = 2;
      const scrollbarHeight = table ? table.offsetHeight - table.clientHeight : 0;
      // Resizing can scroll the page to keep a focused pagination button in view.
      const scrollTop = pageFrame.lastElementChild?.scrollTop ?? 0;
      const availableHeight =
        pageFrame.getBoundingClientRect().bottom -
        tableArea.getBoundingClientRect().top -
        scrollTop -
        pagination.getBoundingClientRect().height -
        bottomPadding -
        headerHeight -
        tableBorders -
        scrollbarHeight;
      const nextPageSize = Math.min(100, Math.max(1, Math.floor(availableHeight / rowHeight)));
      if (nextPageSize !== pageSize) {
        setPageSize(nextPageSize);
        if (hasMeasuredPageSize.current) resetPagination();
      }
      hasMeasuredPageSize.current = true;
    };

    updatePageSize();
    const observer = new ResizeObserver(updatePageSize);
    observer.observe(pageFrame);
    observer.observe(tableArea);
    observer.observe(pagination);
    if (tableArea.previousElementSibling) observer.observe(tableArea.previousElementSibling);
    return () => observer.disconnect();
  }, [pageSize, resetPagination]);

  useEffect(() => {
    const base = filtersToUrl(filters);
    setQueryParams(() => ({
      ...base,
      ...(cursor ? { starting_after: cursor } : {}),
      ...(pageIndex > 0 ? { page_index: String(pageIndex) } : {}),
    }));
  }, [filters, cursor, pageIndex, setQueryParams]);

  const updateFilter = <K extends keyof CardDisputesFilters>(key: K, value: CardDisputesFilters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleFilterReset = () => {
    setFilters(EMPTY_FILTERS);
  };

  const isFilterActive = Boolean(filters.stage || filters.submission_status);

  const isInitialFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialFilterRenderRef.current) {
      isInitialFilterRenderRef.current = false;
      return;
    }
    resetPagination();
  }, [filters, resetPagination]);

  const isInitialPlatformRenderRef = useRef(true);
  useEffect(() => {
    if (isInitialPlatformRenderRef.current) {
      isInitialPlatformRenderRef.current = false;
      return;
    }
    // Switching platform or live/sandbox mode must not keep the previous context's cursor.
    resetPagination();
  }, [currentPlatform?.id, isSandbox, resetPagination]);

  const {
    data: response,
    isError,
    isLoading,
    isFetching,
    refetch,
  } = useCardDisputes({
    query: {
      stage: filters.stage || undefined,
      submission_status: filters.submission_status || undefined,
    },
    cursor,
    pageSize,
  });

  const cardDisputes = useMemo(() => response?.card_disputes ?? [], [response]);
  const hasMore = response?.has_more ?? false;
  const tableMinHeight = cardDisputes.length === 0 ? 341 : undefined;

  const handleTimeOptionChange = (value: TableTimeFormat | undefined) => {
    if (!value) return;
    updateTableConfig({ timeFormat: value }).catch(() => {
      addDangerNotification({ content: 'Failed to save time format preference' });
    });
  };

  const handlePageNext = () => {
    if (cardDisputes.length > 0) {
      onNext({ cursor: cardDisputes[cardDisputes.length - 1].id });
    }
  };

  const columns: DataTableColumnDef<CardDispute>[] = [
    {
      accessor: 'id',
      header: 'Dispute ID',
      width: 'minmax(200px, 1.5fr)',
      className: 'min-w-0',
      cell: ({ row }) => <span className="min-w-0 truncate font-mono text-xs">{row.id}</span>,
    },
    {
      accessor: 'card_transaction_id',
      header: 'Transaction',
      width: 'minmax(220px, 1.5fr)',
      className: 'min-w-0',
      cell: ({ row }) => (
        <a
          className="min-w-0 truncate font-mono text-xs text-primary underline-offset-4 hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            navigate(`${ROUTE.CARD_TRANSACTIONS}/edit/${row.card_transaction_id}`);
          }}
        >
          {row.card_transaction_id}
        </a>
      ),
    },
    {
      accessor: 'reason',
      header: 'Reason',
      width: 'minmax(min-content, 180px)',
      className: 'min-w-0',
      cell: ({ row }) => <span className="truncate">{getDisputeReasonLabel(row.reason)}</span>,
    },
    {
      accessor: 'workflow',
      header: 'Workflow',
      width: 'minmax(min-content, 130px)',
      cell: ({ row }) => <span className="text-muted-foreground">{getWorkflowLabel(row.workflow)}</span>,
    },
    {
      accessor: 'stage',
      header: 'Stage',
      width: 'minmax(min-content, 170px)',
      cell: ({ row }) => <span>{getDisputeStageLabel(row.stage)}</span>,
    },
    {
      accessor: 'status',
      header: 'Status',
      width: 'minmax(min-content, 140px)',
      cell: ({ row }) => (
        <Badge variant="ghost" className={getTransferBadgeClassName()}>
          {row.status === 'closed' ? (
            <Icon.CircleCheck />
          ) : row.status === 'open' ? (
            <Icon.CirclePendingOne />
          ) : (
            <Icon.Warning />
          )}
          {getDisputeStatusLabel(row.status)}
        </Badge>
      ),
    },
    {
      accessor: 'amount',
      header: 'Amount',
      width: 'min-content',
      cell: ({ row }) => <span>{formatNumber(row.amount, row.currency)}</span>,
    },
    {
      accessor: 'submission_status',
      header: 'Submission',
      width: 'minmax(min-content, 160px)',
      cell: ({ row }) => (
        <Badge
          variant="ghost"
          className={getTransferBadgeClassName(
            row.submission_status === 'failed'
              ? 'danger'
              : row.submission_status === 'submitted'
                ? 'success'
                : 'secondary'
          )}
        >
          {row.submission_status === 'submitted' ? (
            <Icon.CircleCheck />
          ) : row.submission_status === 'failed' ? (
            <Icon.CircleCross />
          ) : row.submission_status === 'requires_action' ? (
            <Icon.Warning />
          ) : row.submission_status !== 'not_required' ? (
            <Icon.CirclePendingApproval />
          ) : null}
          {getSubmissionStatusLabel(row.submission_status)}
        </Badge>
      ),
    },
    {
      accessor: 'respond_by',
      header: 'Respond By',
      width: 'min-content',
      cell: ({ row }) => <span className="whitespace-nowrap">{formatRespondBy(row.respond_by)}</span>,
    },
    {
      accessor: 'created_at',
      header: (
        <Dropdown
          size="small"
          positionRight
          active={timeFormat}
          options={[
            { label: 'Relative Time', value: TableTimeFormat.Relative },
            { label: 'Date / Time', value: TableTimeFormat.Absolute },
          ]}
          onChange={handleTimeOptionChange}
          customLabel={
            <Flex alignItems="center" gap={4}>
              Created <Icon.ChevronDown />
            </Flex>
          }
        />
      ),
      width: 'min-content',
      cell: ({ row }) => (
        <Timestamp
          date={row.created_at}
          align="right"
          className="text-xs text-muted-foreground"
          formatHover={(hoverDate) =>
            formatDateTime(hoverDate, {
              timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc',
            })
          }
        >
          {timeFormat === TableTimeFormat.Relative
            ? getFormattedDate(row.created_at, 'time-passed')
            : formatDateTime(row.created_at, { timeZoneFormat: 'local' })}
        </Timestamp>
      ),
    },
  ];

  return (
    <Inner pt={0} px="var(--page-margin)">
      <Toolbar>
        <ToolbarGroup>
          <DisputeFilterDropdown
            size="small"
            active={filters.stage}
            $isFilterActive={Boolean(filters.stage)}
            options={STAGE_OPTIONS}
            label={STAGE_OPTIONS.find((option) => option.value === filters.stage)?.label ?? 'Stage'}
            onChange={(value: StageFilter) => updateFilter('stage', value)}
          />
          <DisputeFilterDropdown
            size="small"
            active={filters.submission_status}
            $isFilterActive={Boolean(filters.submission_status)}
            options={SUBMISSION_STATUS_OPTIONS}
            label={
              SUBMISSION_STATUS_OPTIONS.find((option) => option.value === filters.submission_status)?.label ??
              'Submission'
            }
            onChange={(value: SubmissionStatusFilter) => updateFilter('submission_status', value)}
          />
          {isFilterActive && (
            <ResetFilter size="small" variant="subtle-primary" icon={<Icon.Reset />} onClick={handleFilterReset}>
              Reset
            </ResetFilter>
          )}
        </ToolbarGroup>
        <ToolbarGroup>
          {canManage && (
            <Button
              size="small"
              variant="primary"
              icon={<Icon.Plus />}
              onClick={() => navigate(ROUTE.CARD_DISPUTES_NEW)}
              data-testid="card-disputes-new"
            >
              New dispute
            </Button>
          )}
        </ToolbarGroup>
      </Toolbar>

      <div ref={tableAreaRef} data-testid="card-disputes-table-area">
        <Loading isLoading={isLoading}>
          <DataTable<CardDispute>
            columns={columns}
            data={cardDisputes}
            rowKey={(row) => row.id}
            isLoading={isFetching}
            loadingContent={<LogoLoading />}
            style={{ minHeight: tableMinHeight }}
            emptyContent={
              isError ? (
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.Warning />
                    </EmptyMedia>
                    <EmptyTitle>Error loading disputes</EmptyTitle>
                    <EmptyDescription>Something went wrong loading disputes. Try again.</EmptyDescription>
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => {
                        refetch().catch(() => {
                          /* the error state remains visible */
                        });
                      }}
                    >
                      Retry
                    </Button>
                  </EmptyHeader>
                </Empty>
              ) : (
                <Empty className="min-h-[300px]">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icon.Shield />
                    </EmptyMedia>
                    <EmptyTitle>No disputes found</EmptyTitle>
                    <EmptyDescription>
                      {isFilterActive
                        ? 'No disputes were found matching the specified filters'
                        : 'Disputes filed on card transactions will appear here.'}
                    </EmptyDescription>
                  </EmptyHeader>
                </Empty>
              )
            }
            rowClassName="min-h-11"
            onRowClick={(row) => {
              navigate(`${ROUTE.CARD_DISPUTES}/edit/${row.id}`);
            }}
          />
        </Loading>
      </div>
      <div
        ref={paginationRef}
        className="relative z-[1] flex items-center justify-end pt-4"
        data-testid="card-disputes-pagination"
      >
        <Pagination
          current={pageIndex + 1}
          disablePrev={!hasPrevious || isFetching}
          disableNext={!hasMore || isFetching}
          onPrev={onPrevious}
          onNext={handlePageNext}
        />
      </div>
    </Inner>
  );
};

export const PageCardDisputes: React.FC = () => (
  <IssuerDashboardGate>
    <PageCardDisputesContent />
  </IssuerDashboardGate>
);
